import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showModal, hideModal, installModalFallback } from "./modalHelper";
import { API, unwrap, apiMessage, toast, formatAmount } from "./projetsCommon";
import "./GestionProjets.css";

const empty = { matricule: "", nom: "", prenom: "", poste: "", telephone: "", email: "", tauxHoraire: "", actif: true };

const schema = Yup.object({
    nom: Yup.string().trim().required("Le nom est obligatoire").max(100, "100 caractères maximum"),
    email: Yup.string().nullable().email("Email invalide"),
    tauxHoraire: Yup.number().typeError("Taux invalide").nullable().transform((v, o) => (o === "" ? null : v)).min(0, "Taux invalide"),
});

const Employes = () => {
    const [employes, setEmployes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [search, setSearch] = useState("");
    const [showInactifs, setShowInactifs] = useState(false);

    useEffect(() => { fetchEmployes(); }, []);
    useEffect(() => installModalFallback(), []);

    const fetchEmployes = () => {
        axiosInstance.get(API.employes)
            .then((res) => setEmployes(unwrap(res) || []))
            .catch((error) => toast("error", apiMessage(error, "Impossible de charger les employés")))
            .finally(() => setLoading(false));
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return employes.filter((e) => {
            if (!showInactifs && !e.actif) return false;
            if (!term) return true;
            return [e.matricule, e.nom, e.prenom, e.poste, e.telephone, e.email].filter(Boolean).join(" ").toLowerCase().includes(term);
        });
    }, [employes, search, showInactifs]);

    const formik = useFormik({
        initialValues: empty,
        validationSchema: schema,
        onSubmit: async (v, { setErrors }) => {
            const t = (x) => (x && x.trim() ? x.trim() : null);
            const payload = {
                matricule: t(v.matricule), nom: v.nom.trim(), prenom: t(v.prenom), poste: t(v.poste),
                telephone: t(v.telephone), email: t(v.email),
                tauxHoraire: v.tauxHoraire === "" || v.tauxHoraire === null ? null : Number(v.tauxHoraire),
                actif: !!v.actif,
            };
            try {
                if (editing) await axiosInstance.put(API.employe(editing.id), payload);
                else await axiosInstance.post(API.employes, payload);
                hideModal("employeModal");
                toast("success", editing ? "Employé modifié" : "Employé créé");
                fetchEmployes();
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Enregistrement échoué"));
            }
        },
    });

    const openCreate = () => { setEditing(null); formik.resetForm({ values: empty }); showModal("employeModal"); };
    const openEdit = (e) => {
        setEditing(e);
        formik.resetForm({
            values: {
                matricule: e.matricule || "", nom: e.nom || "", prenom: e.prenom || "", poste: e.poste || "",
                telephone: e.telephone || "", email: e.email || "", tauxHoraire: e.tauxHoraire ?? "", actif: !!e.actif,
            },
        });
        showModal("employeModal");
    };

    const handleDelete = (e) => {
        Swal.fire({
            title: `Supprimer ${e.nomComplet} ?`,
            text: e.nbProjets ? "Cet employé est affecté à des projets : désactivez-le plutôt." : "Cette action est définitive.",
            icon: "warning", showCancelButton: true, confirmButtonText: "Oui, supprimer", cancelButtonText: "Annuler",
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            try {
                await axiosInstance.delete(API.employe(e.id));
                toast("success", "Employé supprimé");
                fetchEmployes();
            } catch (error) {
                Swal.fire({ icon: "error", title: "Suppression impossible", text: apiMessage(error, "Opération échouée") });
            }
        });
    };

    const invalid = (n) => (formik.touched[n] && formik.errors[n] ? "is-invalid" : "");
    const field = (name, label, props = {}) => (
        <>
            <label>{label}</label>
            <input name={name} className={`form-control ${invalid(name)}`} value={formik.values[name] ?? ""}
                onChange={formik.handleChange} onBlur={formik.handleBlur} {...props} />
            <div className="invalid-feedback">{formik.errors[name]}</div>
        </>
    );

    return (
        <div className="container-fluid mt-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap my-3">
                <h3 className="mb-2">Employés</h3>
                <button className="btn btn-primary btn-ajouter mb-2" onClick={openCreate}><i className="fas fa-user-plus"></i> Nouvel employé</button>
            </div>

            <div className="d-flex flex-wrap align-items-center mb-3">
                <input className="form-control mr-3 mb-2" style={{ maxWidth: 320 }} placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} />
                <div className="custom-control custom-switch mb-2">
                    <input type="checkbox" className="custom-control-input" id="showInactifs" checked={showInactifs} onChange={(e) => setShowInactifs(e.target.checked)} />
                    <label className="custom-control-label" htmlFor="showInactifs">Afficher les inactifs</label>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-hover table-sm bg-white">
                    <thead className="thead-light">
                        <tr>
                            <th>Matricule</th><th>Nom</th><th>Poste</th><th>Téléphone</th><th>Email</th>
                            <th className="text-right">Taux horaire</th><th className="text-center">Projets</th><th>Statut</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? <tr><td colSpan="9" className="text-center py-4">Chargement...</td></tr>
                            : filtered.length === 0 ? <tr><td colSpan="9" className="text-center text-muted py-4">Aucun employé</td></tr>
                                : filtered.map((e) => (
                                    <tr key={e.id} className={e.actif ? "" : "text-muted"}>
                                        <td>{e.matricule || "—"}</td>
                                        <td><strong>{e.nomComplet}</strong></td>
                                        <td>{e.poste || "—"}</td>
                                        <td>{e.telephone || "—"}</td>
                                        <td>{e.email || "—"}</td>
                                        <td className="text-right">{e.tauxHoraire != null ? formatAmount(e.tauxHoraire) : "—"}</td>
                                        <td className="text-center"><span className="badge badge-light">{e.nbProjets}</span></td>
                                        <td>{e.actif ? <span className="badge badge-success">Actif</span> : <span className="badge badge-secondary">Inactif</span>}</td>
                                        <td className="text-right text-nowrap">
                                            <button className="btn btn-sm btn-outline-secondary mr-1" title="Modifier" onClick={() => openEdit(e)}><i className="fas fa-edit"></i></button>
                                            <button className="btn btn-sm btn-outline-danger" title="Supprimer" onClick={() => handleDelete(e)}><i className="fas fa-trash"></i></button>
                                        </td>
                                    </tr>
                                ))}
                    </tbody>
                </table>
            </div>

            <div className="modal fade" id="employeModal">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5><i className="fas fa-user mr-2"></i>{editing ? "Modifier l'employé" : "Nouvel employé"}</h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group col-md-4">{field("matricule", "Matricule")}</div>
                                <div className="form-group col-md-8">{field("poste", "Poste", { placeholder: "ex : Maçon, Commercial…" })}</div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-6">{field("nom", "Nom *")}</div>
                                <div className="form-group col-md-6">{field("prenom", "Prénom")}</div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-6">{field("telephone", "Téléphone", { type: "tel" })}</div>
                                <div className="form-group col-md-6">{field("email", "Email", { type: "email" })}</div>
                            </div>
                            <div className="form-row align-items-center">
                                <div className="form-group col-md-6">{field("tauxHoraire", "Taux horaire (DH)", { type: "number", step: "0.01", min: 0 })}</div>
                                <div className="form-group col-md-6 pt-4">
                                    <div className="custom-control custom-switch">
                                        <input type="checkbox" className="custom-control-input" id="actif" name="actif"
                                            checked={!!formik.values.actif} onChange={formik.handleChange} />
                                        <label className="custom-control-label" htmlFor="actif">Actif</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button type="button" className="btn btn-enregistrer" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>Sauvegarder</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Employes;
